import { planCompression } from "../compressionPlan";
// A band below CDT0 must be carved out before a restriction row can name it.
import { CdtLayoutOptions, buildFromCdt } from "./buildFromCdt";
import type { ExpressibilityFinding } from "./expressibility";
import { COMPOUND_JOIN, collapse4, sanitizeCdtValue } from "../collapse4";
import { buildLayout } from "../buildLayout";
import { baysOfRun, fencePlacementFor, shelfUniverseOf } from "../restrictionFence";
import { deriveRestrictionPlacement } from "../restrictionPlacement";
// No runtime cycle: variantRestrictionUpload's imports from here are type-only.
import { studioGroupIdFor } from "../variantRestrictionUpload";
import type {
  RestrictionPlacement,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
} from "../studioTypes";
import {
  carvedKeyMapForPromotion,
  effectiveAttrs,
  rekeyScopedLevelOrderForPromotion,
  rekeyScopedMapForPromotion,
} from "../studioTypes";

/** The tree change, ready to apply to a draft. `newValues` is band first. */
export interface PromotionPlan {
  fromAttr: string;
  topAttr: string;
  parentValue: string;
  newValues: readonly string[];
  bandVariant: string;
  /** skuId -> new top-level value. */
  retag: Readonly<Record<string, string>>;
  valueOrder: readonly string[];
}

export interface ColumnTestEntry {
  value: string;
  sliceCm: number;
  /** Widest pack x minFacingsPerShelf. */
  columnFloorCm: number;
  skuCount: number;
  passes: boolean;
}

export interface ColumnTest {
  shelfCount: number;
  perValue: readonly ColumnTestEntry[];
  degenerate: readonly string[];
  passes: boolean;
}

/** The conditions the save endpoint accepts. */
export type RemedyCondition = "MUST_HAVE_ONLY" | "MUST_NOT_HAVE" | "MUST_HAVE" | "CAN_HAVE";

export interface RemedyRow {
  variantGroupId: string;
  variant: string;
  bayNo: string;
  shelf: string;
  condition: RemedyCondition;
}

/** How to express "keep this band to these shelves". "fence" (the default, and the only one the
 * studio offers) writes MUST_NOT_HAVE for the band's own variant on every shelf OUTSIDE it; naming
 * one variant sets no may_have list, so no sibling is evicted. "exclude" and "reserve" are
 * reservations kept only for comparison. */
export type RowFormulation = "fence" | "exclude" | "reserve";

/** The shelf and bay universe a fence is taken against; bays are the WHOLE run's. */
export interface FenceGeometry {
  shelfUniverse: readonly number[];
  bays: readonly number[];
}

export interface DroppedDelta {
  before: number;
  after: number;
  added: readonly string[];
  removed: readonly string[];
}

export interface RestrictionRemedy {
  promotion: PromotionPlan;
  column: ColumnTest;
  shippedVariants: readonly string[];
  droppedDelta: DroppedDelta;
  placement: RestrictionPlacement;
  /** Where the band must NOT go - the complement, and what the rows name. */
  fence: RestrictionPlacement;
  rows: readonly RemedyRow[];
  cells: number;
  bandCells: number;
  siblingNames: readonly string[];
  siblingVariants: number;
  evictions: number;
  capacityCm: number;
  neededCm: number;
  notes: readonly string[];
}

const sum = (values: readonly number[]) => values.reduce((total, value) => total + value, 0);

/** The remedy for one band finding; null when its SKUs straddle two top-level values. */
export const promotionRemedyFor = (
  finding: ExpressibilityFinding,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: CdtLayoutOptions = {},
): RestrictionRemedy | null => {
  if (finding.kind !== "horizontal-band" && finding.kind !== "vertical-band") return null;
  const run = runs.find((candidate) => candidate.run === finding.run);
  if (!run) return null;

  const topAttr = planCompression(items, draft, runs).levels[0]?.levels[0]?.attr;

  if (!topAttr || topAttr === finding.attr) return null;

  const attrsOf = new Map(items.map((item) => [item.skuId, effectiveAttrs(item, draft)]));
  const valueOf = (skuId: string, attr: string) => String(attrsOf.get(skuId)?.[attr] ?? "");

  const bandSkus = new Set(finding.skuIds);
  // A promotion subdivides ONE top-level value; a band spanning two has no plan.
  const parents = new Set(finding.skuIds.map((skuId) => valueOf(skuId, topAttr)));
  if (parents.size !== 1) return null;
  const parentValue = [...parents][0];
  if (!parentValue) return null;

  const family = items.filter(
    (item) => item.run === finding.run && valueOf(item.skuId, topAttr) === parentValue,
  );
  if (family.length === 0) return null;

  // Sanitise here: collapse4 strips ":" before joining, so an unsanitised compound matches no row.
  const compound = (value: string) => sanitizeCdtValue(`${parentValue}${COMPOUND_JOIN}${value}`);

  // Binary, not a full subdivision: carving out only the band's value avoids stranding siblings.
  const bandValue = compound(finding.value);
  const newValues = [bandValue, parentValue];
  const bandSkuIds = new Set(finding.skuIds);
  const retag: Record<string, string> = {};
  for (const item of family) {
    if (bandSkuIds.has(item.skuId)) retag[item.skuId] = bandValue;
  }

  // The band takes its parent's place in the root order: accepting a shelf rule must not move
  // the section along the wall. A parent the root never ordered ranks last.
  const priorTop = draft.scopedValueOrder[""] ?? draft.valueOrder[topAttr] ?? [];
  const siblings = (draft.promotions ?? []).filter(
    (entry) => entry.attr === topAttr && entry.parentValue === parentValue,
  );
  const restoredOverrides = { ...draft.attrOverrides };
  for (const sibling of siblings) {
    for (const skuId of sibling.skuIds) {
      restoredOverrides[skuId] = { ...restoredOverrides[skuId], [topAttr]: parentValue };
    }
  }
  const originalWalk = buildLayout(
    items,
    { ...draft, attrOverrides: restoredOverrides },
    runs,
  ).walkOrder;
  const rank = new Map(originalWalk.map((skuId, index) => [skuId, index]));
  const firstRank = (skuIds: readonly string[]) =>
    Math.min(...skuIds.map((skuId) => rank.get(skuId) ?? Infinity));
  const newRank = firstRank(finding.skuIds);
  const successor = priorTop.find((value) =>
    siblings.some(
      (sibling) => sibling.bandVariant === value && firstRank(sibling.skuIds) > newRank,
    ),
  );
  // Insert only the new band; existing groups may have been explicitly reordered.
  const valueOrder = [...priorTop];
  const before = successor ?? parentValue;
  const at = valueOrder.indexOf(before);
  if (at < 0) valueOrder.push(...newValues);
  else valueOrder.splice(at, 0, bandValue);

  const promotion: PromotionPlan = {
    fromAttr: finding.attr,
    topAttr,
    parentValue,
    newValues,
    bandVariant: bandValue,
    retag,
    valueOrder,
  };
  const promotedAttrsOf = (item: StudioItem) => retag[item.skuId] ?? valueOf(item.skuId, topAttr);

  const promotedDraft = applyPromotion(draft, promotion, items);
  const { report: after } = buildFromCdt(items, runs, promotedDraft, options);

  // The row's variant is read back from the collapsed contract; the backend matches it literally.
  const collapsed = collapse4(items, promotedDraft, runs, buildLayout(items, promotedDraft, runs));
  const cdt0Of = (skuId: string) => collapsed.contract.itemValues[skuId]?.[0] ?? "";
  // Category-wide, not run-scoped: the row carries no run identifier.
  const shippedVariants = [...new Set(items.map((item) => cdt0Of(item.skuId)))]
    .filter((value) => value !== "")
    .sort();

  const bandItems = items.filter((item) => bandSkus.has(item.skuId));
  const shippedBand = [...new Set(bandItems.map((item) => cdt0Of(item.skuId)))];
  // Structural guard: with no single shipped CDT0 value there is no row to emit.
  if (shippedBand.length !== 1 || !shippedBand[0]) return null;
  const bandVariant = shippedBand[0];
  if (!shippedVariants.includes(bandVariant)) return null;

  const shelfCount = run.shelves.length;
  const perValue = newValues.map((value) => {
    const own = items.filter(
      (item) => item.run === finding.run && String(promotedAttrsOf(item) ?? "") === value,
    );
    const sliceCm = sum(
      after.windows
        .filter((window) => window.run === finding.run && window.path[0] === value)
        .map((window) => window.width),
    );
    const columnFloorCm =
      Math.max(...own.map((item) => item.facingWidthCm), 0) * (options.minFacingsPerShelf ?? 2);
    return {
      value,
      sliceCm: +sliceCm.toFixed(2),
      columnFloorCm: +columnFloorCm.toFixed(2),
      skuCount: own.length,
      passes: sliceCm >= columnFloorCm && own.length >= shelfCount,
    };
  });
  const column: ColumnTest = {
    shelfCount,
    perValue,
    degenerate: perValue.filter((entry) => !entry.passes).map((entry) => entry.value),
    passes: perValue.every((entry) => entry.passes),
  };

  const placement = deriveRestrictionPlacement(finding.skuIds, items, runs);
  if (!placement) return null;
  const geometry: FenceGeometry = {
    shelfUniverse: shelfUniverseOf(run),
    bays: baysOfRun(run),
  };
  const fence = fenceCellsFor(placement, geometry);
  if (!fence) return null;
  // Content-derived group id, so re-finalizing replaces the group instead of appending a copy.
  const rows = rowsForCells(
    studioGroupIdFor("PROMOTED", bandVariant, finding.run),
    bandVariant,
    placement,
    geometry,
    shippedVariants,
  );
  if (rows.length === 0) return null;

  const cost = costOfRows(rows, bandVariant, shippedVariants);
  const bandCells = sum(placement.cells.map((cell) => cell.bays.length));

  // Capacity is asked of the cells the band KEEPS, not of the fenced ones.
  const capacityCm = bandCells * run.bayWidthCm;
  const neededCm = sum(
    bandItems.flatMap((item) =>
      item.placements.filter((p) => p.run === finding.run).map((p) => p.widthCm),
    ),
  );

  const beforeDropped = new Set(buildFromCdt(items, runs, draft, options).report.dropped);
  const afterDropped = new Set(after.dropped);
  const droppedDelta = {
    before: beforeDropped.size,
    after: afterDropped.size,
    added: [...afterDropped].filter((skuId) => !beforeDropped.has(skuId)),
    removed: [...beforeDropped].filter((skuId) => !afterDropped.has(skuId)),
  };

  const notes = [
    `promote "${finding.value}" from "${finding.attr}" into "${topAttr}": "${parentValue}" becomes ${newValues.length} compound CDT0 values, every other ${topAttr} value untouched`,
    `"${bandVariant}" is kept to shelf ${placement.cells.map((cell) => cell.shelf).join(", ")}, written as MUST_NOT_HAVE on shelf ${fence.cells.map((cell) => cell.shelf).join(", ")} across every bay of the run`,
    `the band keeps ${bandCells} (bay, shelf) cells supplying ${capacityCm.toFixed(0)} cm; it occupies ${neededCm.toFixed(0)} cm today — ${capacityCm >= neededCm ? "FITS" : "DOES NOT FIT"}`,
    cost.evictions === 0
      ? `cost: none. The ${rows.length} rows name one variant, so no may_have list is set anywhere, permits_all stays true, and every sibling keeps every cell it had — including the shelves the band is kept to`
      : `cost: ${cost.siblingVariants} sibling CDT0 variants losing ${cost.evictions} (variant, bay, shelf) triples`,
    column.passes
      ? "every value the split creates can stand as a block"
      : `the split strands ${column.degenerate.join(", ")} — no row makes a strip into a block`,
    `dropped SKUs ${droppedDelta.before} -> ${droppedDelta.after}, which is ${droppedDelta.added.length} newly stranded and ${droppedDelta.removed.length} rescued`,
    "buildFromCdt models NO restrictions, so its rebuild of this band does not change when a row is added and its shelf-exactness is not evidence that the row works. What is measured here is that the promoted TREE is well formed; proving the arrangement needs a pogger solve with the row applied",
  ];

  return {
    promotion: { ...promotion, bandVariant },
    column,
    placement,
    fence,
    rows,
    cells: cost.cells,
    bandCells,
    shippedVariants,
    siblingNames: cost.siblingNames,
    droppedDelta,
    siblingVariants: cost.siblingVariants,
    evictions: cost.evictions,
    capacityCm: +capacityCm.toFixed(2),
    neededCm: +neededCm.toFixed(2),
    notes,
  };
};

/** Apply a promotion to a draft: a retag plus the top level's new order. */
export const applyPromotion = (
  draft: StudioDraft,
  promotion: PromotionPlan,
  items: readonly StudioItem[],
): StudioDraft => {
  // Finalize replays saved offers; their old root orders must not undo later edits.
  const retags = Object.entries(promotion.retag);
  if (
    retags.length > 0 &&
    retags.every(([skuId, value]) => draft.attrOverrides[skuId]?.[promotion.topAttr] === value) &&
    (draft.scopedValueOrder[""] ?? draft.valueOrder[promotion.topAttr] ?? []).includes(
      promotion.bandVariant,
    )
  )
    return draft;
  const keyMap = carvedKeyMapForPromotion(
    items,
    draft,
    {
      parentValue: promotion.parentValue,
      bandVariant: promotion.bandVariant,
      skuIds: Object.keys(promotion.retag),
    },
    [
      ...Object.keys(draft.scopedValueOrder),
      ...Object.keys(draft.bandOverrides),
      ...Object.keys(draft.orientOverrides),
      ...Object.keys(draft.scopedLevelOrder ?? {}),
    ],
  );
  const attrOverrides = { ...draft.attrOverrides };
  for (const [skuId, value] of Object.entries(promotion.retag)) {
    attrOverrides[skuId] = { ...attrOverrides[skuId], [promotion.topAttr]: value };
  }
  // The branch's LEVEL order is keyed to the old root value too.
  const scopedLevelOrder = rekeyScopedLevelOrderForPromotion(
    draft.scopedLevelOrder,
    keyMap,
    promotion.parentValue,
    promotion.bandVariant,
  );
  return {
    ...draft,
    attrOverrides,
    valueOrder: { ...draft.valueOrder, [promotion.topAttr]: promotion.valueOrder },
    // Keyed to the OLD root value: replace the root entry or the split values re-rank.
    scopedValueOrder: {
      ...rekeyScopedMapForPromotion(draft.scopedValueOrder, keyMap),
      "": promotion.valueOrder,
    },
    bandOverrides: rekeyScopedMapForPromotion(draft.bandOverrides, keyMap),
    orientOverrides: rekeyScopedMapForPromotion(draft.orientOverrides, keyMap),
    ...(scopedLevelOrder ? { scopedLevelOrder } : {}),
  };
};

/* ── the proof ─────────────────────────────────────────────────────────── */

export interface AllowListProof {
  named: number;
  /** Named cells where the band's variant is the only one still available. */
  exclusive: number;
  untouched: number;
  evicted: readonly string[];
  compelled: number;
  empty: number;
  ok: boolean;
}

/** What a row set does to the model's option space: which (variant, bay, shelf) triples survive,
 * not what the solver picks. `translate_shelves_may_have` removes must_have and must_not_have from
 * may_have, and availability is must_be_on + may_be_on. `shippedVariants` MUST be the CDT0 list
 * the collapsed contract actually emits. */
export const proveAllowLists = (
  rows: readonly RemedyRow[],
  shippedVariants: readonly string[],
  bays: readonly number[],
  shelves: readonly number[],
  bandVariant: string,
): AllowListProof => {
  const key = (bay: number, shelf: number) => `${bay}|${shelf}`;
  const mayHaveNamed = new Map<string, Set<string>>();
  const mustHave = new Map<string, Set<string>>();
  const mustNotHave = new Map<string, Set<string>>();
  const add = (map: Map<string, Set<string>>, cell: string, variant: string) => {
    const set = map.get(cell) ?? new Set<string>();
    set.add(variant);
    map.set(cell, set);
  };

  for (const row of rows) {
    const shelf = Number(row.shelf);
    if (!Number.isFinite(shelf)) continue;
    for (const bayText of row.bayNo.split("|")) {
      const bay = Number(bayText.trim());
      if (!Number.isFinite(bay)) continue;
      const cell = key(bay, shelf);
      const condition = row.condition as string;
      if (condition === "CAN_HAVE" || condition === "MUST_HAVE_ONLY") {
        add(mayHaveNamed, cell, row.variant);
      }
      if (condition === "MUST_HAVE" || condition === "MUST_HAVE_ONLY") {
        add(mustHave, cell, row.variant);
      }
      if (condition === "MUST_NOT_HAVE") add(mustNotHave, cell, row.variant);
    }
  }

  const touched = new Set([...mayHaveNamed.keys(), ...mustHave.keys(), ...mustNotHave.keys()]);
  let exclusive = 0;
  let untouched = 0;
  let compelled = 0;
  let empty = 0;
  const evicted = new Set<string>();

  for (const bay of bays) {
    for (const shelf of shelves) {
      const cell = key(bay, shelf);
      if (!touched.has(cell)) {
        untouched += 1;
        continue;
      }
      const named = mayHaveNamed.get(cell);
      const must = mustHave.get(cell) ?? new Set<string>();
      const mustNot = mustNotHave.get(cell) ?? new Set<string>();
      const mayHave = new Set(named ? [...named] : shippedVariants);
      for (const variant of must) mayHave.delete(variant);
      for (const variant of mustNot) mayHave.delete(variant);

      const available = shippedVariants.filter(
        (variant) => must.has(variant) || mayHave.has(variant),
      );
      if (available.length === 0) empty += 1;
      if (available.length === 1 && available[0] === bandVariant) exclusive += 1;
      for (const variant of shippedVariants) {
        if (!must.has(variant) && !mayHave.has(variant)) evicted.add(variant);
      }
      if (must.has(bandVariant)) compelled += 1;
    }
  }

  const named = [...touched].filter((cell) => {
    const [bay, shelf] = cell.split("|").map(Number);
    return bays.includes(bay) && shelves.includes(shelf);
  }).length;

  return {
    named,
    exclusive,
    untouched,
    evicted: [...evicted].sort(),
    compelled,
    empty,
    // Proven only when every named cell holds the band alone, compelled, and nobody is stranded.
    ok: named > 0 && exclusive === named && compelled === named && empty === 0,
  };
};

export interface DirectRestriction {
  variant: string;
  placement: RestrictionPlacement;
  /** Where the band must NOT go - the complement, and what the rows name. */
  fence: RestrictionPlacement;
  rows: readonly RemedyRow[];
  cells: number;
  bandCells: number;
  shippedVariants: readonly string[];
  siblingNames: readonly string[];
  siblingVariants: number;
  evictions: number;
  notes: readonly string[];
}

/**
 * Rows for a band ALREADY at the top level. The variant is read back from `collapse4`'s contract,
 * never from `finding.value`: the raw attribute value can name a variant that never ships.
 */
export const restrictionRowsFor = (
  finding: ExpressibilityFinding,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: CdtLayoutOptions = {},
  formulation: RowFormulation = "fence",
): DirectRestriction | null => {
  if (finding.kind !== "vertical-band") return null;
  if (finding.carrier.kind !== "restriction-row") return null;
  const run = runs.find((candidate) => candidate.run === finding.run);
  if (!run) return null;

  const topAttr = planCompression(items, draft, runs).levels[0]?.levels[0]?.attr;
  // Verify the carrier's promise: a row on anything deeper than CDT0 reaches nothing.
  if (topAttr !== finding.attr) return null;

  const collapsed = collapse4(items, draft, runs, buildLayout(items, draft, runs));
  const cdt0Of = (skuId: string) => collapsed.contract.itemValues[skuId]?.[0] ?? "";
  const shippedVariants = [...new Set(items.map((item) => cdt0Of(item.skuId)))]
    .filter((value) => value !== "")
    .sort();

  const shippedBand = [...new Set(finding.skuIds.map(cdt0Of))].filter((value) => value !== "");
  if (shippedBand.length !== 1) return null;
  const variant = shippedBand[0];
  if (!shippedVariants.includes(variant)) return null;

  const placement = deriveRestrictionPlacement(finding.skuIds, items, runs);
  if (!placement) return null;

  // The bays are the WHOLE run's, or the band reappears one bay over.
  const geometry: FenceGeometry = {
    shelfUniverse: shelfUniverseOf(run),
    bays: baysOfRun(run),
  };
  const fence = fenceCellsFor(placement, geometry);
  // A band on every shelf has an empty complement. No fence, not a weak one.
  if (!fence) return null;

  // Content-derived id: re-finalizing replaces the group rather than suffixing a copy.
  const rows = rowsForCells(
    studioGroupIdFor("BAND", variant, finding.run, formulation),
    variant,
    placement,
    geometry,
    shippedVariants,
    formulation,
  );
  if (rows.length === 0) return null;

  const cost = costOfRows(rows, variant, shippedVariants);
  const bandCells = sum(placement.cells.map((cell) => cell.bays.length));
  const bandShelves = placement.cells.map((cell) => cell.shelf).join(", ");
  const fencedShelves = fence.cells.map((cell) => cell.shelf).join(", ");
  return {
    variant,
    placement,
    fence,
    rows,
    cells: cost.cells,
    bandCells,
    shippedVariants,
    siblingNames: cost.siblingNames,
    siblingVariants: cost.siblingVariants,
    evictions: cost.evictions,
    notes: [
      `"${variant}" is already a CDT0 value, so no tree change is needed — the row binds directly`,
      `"${variant}" is kept to shelf ${bandShelves}, which is written as MUST_NOT_HAVE on shelf ${fencedShelves} across every bay of the run`,
      `${rows.length} rows over ${cost.cells} (bay, shelf) cells`,
      cost.evictions === 0
        ? "cost: no sibling is excluded from anything — the rows name one variant, so permits_all stays true on every cell and shelves the band keeps are still shared"
        : `cost: ${cost.siblingVariants} sibling CDT0 variants losing ${cost.evictions} (variant, bay, shelf) triples`,
      "buildFromCdt models no restrictions, so it cannot show this arrangement; proving it needs a pogger solve with the rows applied",
    ],
  };
};

/** The fence placement for a band: the shelves it must not appear on. */
export const fenceCellsFor = (
  placement: RestrictionPlacement,
  geometry: FenceGeometry,
): RestrictionPlacement | null =>
  fencePlacementFor(placement, geometry.shelfUniverse, geometry.bays);

/** Rows for one band. "fence" emits one MUST_NOT_HAVE per shelf outside the band, naming the
 * band's own variant over the whole run's bays. Bays are enumerated, never "*", which expands to
 * the whole fixture. Every name emitted must be in the shipped CDT0 list. */
export const rowsForCells = (
  variantGroupId: string,
  variant: string,
  placement: RestrictionPlacement,
  geometry: FenceGeometry,
  shippedVariants: readonly string[],
  formulation: RowFormulation = "fence",
): RemedyRow[] => {
  if (formulation === "fence") {
    const fence = fenceCellsFor(placement, geometry);
    if (!fence) return [];
    return fence.cells.map((cell) => ({
      variantGroupId,
      variant,
      bayNo: cell.bays.join(" | "),
      shelf: String(cell.shelf),
      condition: "MUST_NOT_HAVE" as const,
    }));
  }
  if (formulation === "reserve") {
    return placement.cells.map((cell) => ({
      variantGroupId,
      variant,
      bayNo: cell.bays.join(" | "),
      shelf: String(cell.shelf),
      condition: "MUST_HAVE_ONLY" as const,
    }));
  }
  const others = shippedVariants.filter((candidate) => candidate !== variant);
  return placement.cells.flatMap((cell) =>
    others.map((other) => ({
      variantGroupId,
      variant: other,
      bayNo: cell.bays.join(" | "),
      shelf: String(cell.shelf),
      condition: "MUST_NOT_HAVE" as const,
    })),
  );
};

/** What a row set costs, counted from the rows: a fence names one variant, so evictions are 0. */
export const costOfRows = (
  rows: readonly RemedyRow[],
  bandVariant: string,
  shippedVariants: readonly string[],
): { cells: number; siblingVariants: number; evictions: number; siblingNames: string[] } => {
  const cellKeys = new Set<string>();
  const lost = new Map<string, Set<string>>();
  const allowed = new Map<string, Set<string>>();

  for (const row of rows) {
    for (const bayText of row.bayNo.split("|")) {
      const bay = bayText.trim();
      if (bay === "") continue;
      const cell = `${bay}|${row.shelf}`;
      cellKeys.add(cell);
      if (row.condition === "MUST_NOT_HAVE") {
        const set = lost.get(cell) ?? new Set<string>();
        set.add(row.variant);
        lost.set(cell, set);
        continue;
      }
      if (row.condition === "CAN_HAVE" || row.condition === "MUST_HAVE_ONLY") {
        const set = allowed.get(cell) ?? new Set<string>();
        set.add(row.variant);
        allowed.set(cell, set);
      }
      // MUST_HAVE alone sets no may_have list, so it evicts nobody.
    }
  }

  // An allow-list turns permits_all off, so every variant it does not name loses that cell.
  for (const [cell, named] of allowed) {
    const set = lost.get(cell) ?? new Set<string>();
    for (const variant of shippedVariants) if (!named.has(variant)) set.add(variant);
    lost.set(cell, set);
  }

  // The band losing the fenced cells is the rule working, not a cost.
  const perVariant = new Map<string, number>();
  for (const variants of lost.values()) {
    for (const variant of variants) {
      if (variant === bandVariant) continue;
      perVariant.set(variant, (perVariant.get(variant) ?? 0) + 1);
    }
  }
  const siblingNames = [...perVariant.keys()].sort();
  return {
    cells: cellKeys.size,
    siblingVariants: siblingNames.length,
    evictions: [...perVariant.values()].reduce((total, count) => total + count, 0),
    siblingNames,
  };
};
